/**
 * What the accounting discipline can change, and nothing else.
 *
 * This was the first half of a single Settings page that held both disciplines'
 * assumptions. The file already drew the line and said why: everything here shapes the
 * STATEMENTS, so changing a rate moves the P&L, the control account and the balance
 * sheet together. Everything in `valuation/ValuationSettingsV2.tsx` is read only by
 * the valuation, which consumes finished statements and cannot alter one.
 *
 * Mike's workbook draws the same line - its Settings sheet has a VALUATION: block -
 * and a valuation user handed a lease term and a capital allowance rate has been given
 * a page of controls that cannot change their answer.
 *
 * Written for v2 rather than sharing v1's AssumptionsView, which also edits historical
 * data and a `cogsRatio` that drives v1's cost of sales. v2 takes cost of sales from
 * the COGS tab, so presenting that control here would offer a knob that changes
 * nothing - worse than not offering it.
 *
 * The forecast period is applied on a button rather than as you type: changing it
 * re-keys every period in the model, and doing that on each keystroke would discard
 * figures the user was halfway through entering.
 */

import { useState } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { getForecastContext } from '../../../types/financial-model';
import type { ModelSettings } from '../../../types/financial-model';
import { Field, NumberField, inputClass } from '../settingsFields';

/** The longest forecast either accounting model runs (MAX_FORECAST_YEARS on the server). */
export const MAX_FORECAST_YEARS = 5;

export function AccountingSettingsV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateSetting = useFinancialModelV2Store((s) => s.updateSetting);
  const updateInputValue = useFinancialModelV2Store((s) => s.updateInputValue);
  const updatePeriod = useFinancialModelV2Store((s) => s.updatePeriod);
  const isRecalculating = useFinancialModelV2Store((s) => s.isRecalculating);
  const [splitMethod, setSplitMethod] = useState<'third_month' | 'equal_spread'>(
    'third_month',
  );

  const settings = model?.settings;
  const [startYear, setStartYear] = useState<number | null>(null);
  const [numYears, setNumYears] = useState<number | null>(null);
  const [periodType, setPeriodType] = useState<
    ModelSettings['periodType'] | null
  >(null);

  if (!model || !settings) return null;

  const pendingStart = startYear ?? settings.forecastStartYear;
  const pendingYears = numYears ?? settings.numForecastYears;
  const pendingType = periodType ?? settings.periodType;
  // Years the accounts cover, from the first year to the last filed one: the forecast follows.
  const filed = (settings.historicalYears ?? []).filter((y) => y >= pendingStart);
  const filedYears = filed.length ? Math.max(...filed) - pendingStart + 1 : 0;
  const periodChanged =
    pendingStart !== settings.forecastStartYear ||
    pendingYears !== settings.numForecastYears ||
    pendingType !== settings.periodType;
  return (
    <div className="h-full overflow-auto p-6">
      <h2 className="mb-4 text-lg font-bold text-gray-800">IAS accounting settings</h2>

      <p className="mb-3 max-w-4xl text-xs text-gray-500">
        These shape the statements. Changing one moves the P&amp;L, the control
        account and the balance sheet.
      </p>

      <div className="grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">
            Forecast period
          </h3>
          <div className="space-y-3">
            <NumberField
              label="First year"
              value={pendingStart}
              onChange={setStartYear}
            />
            {/* The filed years are what the accounts cover; the forecast runs after the last
                of them, for at most five years (MAX_FORECAST_YEARS, enforced by the server). */}
            <Field
              label="Forecast years"
              hint={
                filedYears > 0
                  ? `After the ${filedYears} filed year${filedYears === 1 ? '' : 's'} (to ${pendingStart + filedYears - 1}). At most ${MAX_FORECAST_YEARS}.`
                  : `From the first year. At most ${MAX_FORECAST_YEARS}.`
              }
            >
              <select
                value={Math.min(Math.max(pendingYears - filedYears, 1), MAX_FORECAST_YEARS)}
                onChange={(e) => setNumYears(filedYears + Number(e.target.value))}
                className={inputClass}
              >
                {Array.from({ length: MAX_FORECAST_YEARS }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n} year{n === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Period type"
              hint="Changing this re-keys every figure. Coarser periods sum; finer ones split."
            >
              <select
                value={pendingType}
                onChange={(e) =>
                  setPeriodType(e.target.value as typeof settings.periodType)
                }
                className={inputClass}
              >
                <option value="annual">Annual</option>
                <option value="quarterly">Quarterly</option>
                <option value="monthly">Monthly</option>
              </select>
            </Field>

            {pendingType !== settings.periodType && (
              <Field
                label="When splitting into shorter periods"
                hint="Balances always move to the final period regardless."
              >
                <select
                  value={splitMethod}
                  onChange={(e) =>
                    setSplitMethod(e.target.value as typeof splitMethod)
                  }
                  className={inputClass}
                >
                  <option value="third_month">Put the whole amount in the last period</option>
                  <option value="equal_spread">Spread it evenly</option>
                </select>
              </Field>
            )}

            <button
              type="button"
              disabled={!periodChanged || isRecalculating}
              onClick={async () => {
                // One call, not three settings patches: the server re-keys the
                // figures as part of the same change.
                await updatePeriod({
                  forecastStartYear: pendingStart,
                  numForecastYears: pendingYears,
                  periodType: pendingType,
                  method: splitMethod,
                });
                setStartYear(null);
                setNumYears(null);
                setPeriodType(null);
              }}
              className="w-full rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-accent-700 disabled:opacity-40"
            >
              {periodChanged ? 'Apply period change' : 'Period up to date'}
            </button>
            {periodChanged && (
              <p className="text-xs text-amber-700">
                Figures are carried across, and the model is saved to history
                first so this can be undone. Overridden cells are cleared —
                they were set against the old periods. Years added past the
                filing get a starting forecast from its last year.
              </p>
            )}
            {/* What that starting forecast assumed - every figure in those years is
                an assumption to replace, never one the filing supplied. */}
            {model.forecastNotes && model.forecastNotes.length > 0 && (
              <div className="rounded-lg border border-accent-200 bg-accent-50 p-3 text-xs text-gray-700">
                <div className="font-semibold text-accent-700">How the forecast years were filled</div>
                <ul className="mt-1.5 list-disc space-y-1 pl-4">
                  {model.forecastNotes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Rates</h3>
          <div className="space-y-3">
            <Field label="Base currency">
              <input
                value={settings.baseCurrency}
                onChange={(e) => updateSetting('baseCurrency', e.target.value)}
                className={inputClass}
              />
            </Field>
            <NumberField
              label="Tax rate"
              hint="Applied to profit before tax, after any losses brought forward."
              value={settings.taxRate}
              onChange={(v) => updateSetting('taxRate', v)}
              step={0.01}
              suffix={`= ${(settings.taxRate * 100).toFixed(1)}%`}
            />

            {/* The UK has had three main rates in four years, so one rate
                across a five-year model is wrong in at least one of them by
                construction. The single rate above stays the default; these
                override it for the years they name. */}
            <div>
              <span className="text-sm font-medium text-gray-700">
                Tax rate by year
              </span>
              <span className="mt-1 block text-xs text-gray-500">
                Overrides the rate above where given. Blank uses the rate above.
              </span>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {getForecastContext(settings).fiscalYears.map((year) => {
                  const key = String(year);
                  const override = settings.taxRateByYear?.[key];
                  return (
                    <label key={year} className="block">
                      <span className="text-xs text-gray-500">{year}</span>
                      <input
                        type="number"
                        step={0.01}
                        value={override ?? ''}
                        placeholder={settings.taxRate.toFixed(2)}
                        onChange={(e) => {
                          const next = { ...(settings.taxRateByYear ?? {}) };
                          if (e.target.value === '') delete next[key];
                          else next[key] = Number(e.target.value);
                          updateSetting('taxRateByYear', next);
                        }}
                        className={inputClass}
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Off by default. An imported filing states its own deferred tax,
                and a stated figure beats a derived one — but a forecast has no
                stated figure, so a company whose capital allowances run ahead
                of its depreciation shows no deferred tax building at all. */}
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={settings.deriveDeferredTax ?? false}
                onChange={(e) =>
                  updateSetting('deriveDeferredTax', e.target.checked)
                }
                className="mt-0.5"
              />
              <span>
                <span className="text-sm font-medium text-gray-700">
                  Derive deferred tax
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  From the gap between the accounts&rsquo; net book value and the
                  tax written-down value, instead of carrying the filed balance
                  forward. Replaces the filed balance rather than adding to it.
                </span>
              </span>
            </label>

            {settings.deriveDeferredTax && (
              <NumberField
                label="Capital allowance rate"
                hint="Reducing balance. 0.18 is the UK main pool rate."
                value={settings.capitalAllowanceRate ?? 0.18}
                onChange={(v) => updateSetting('capitalAllowanceRate', v)}
                step={0.01}
                suffix={`= ${((settings.capitalAllowanceRate ?? 0.18) * 100).toFixed(1)}%`}
              />
            )}

            {/* Off by default: a historical year's finance costs already
                include the lease interest as filed, and charging it again
                doubles it. A forecast has no filed figure, which is where the
                liability stopped unwinding - it held its last reported balance
                for ever while the right-of-use asset depreciated to nothing. */}
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={settings.deriveLeaseSchedule ?? false}
                onChange={(e) =>
                  updateSetting('deriveLeaseSchedule', e.target.checked)
                }
                className="mt-0.5"
              />
              <span>
                <span className="text-sm font-medium text-gray-700">
                  Derive lease schedule
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  Unwinds the lease liability with interest and splits each
                  payment into interest and capital. Takes over the lease
                  liability rows, so the obligation is not counted twice.
                </span>
              </span>
            </label>

            {settings.deriveLeaseSchedule && (
              <>
                <NumberField
                  label="Lease discount rate"
                  hint="The incremental borrowing rate. IFRS 16 paragraph 26."
                  value={settings.leaseDiscountRate ?? 0.05}
                  onChange={(v) => updateSetting('leaseDiscountRate', v)}
                  step={0.01}
                  suffix={`= ${((settings.leaseDiscountRate ?? 0.05) * 100).toFixed(1)}%`}
                />
                <NumberField
                  label="Lease term (months)"
                  hint="Used to pay the liability down where no payment is entered."
                  value={settings.leaseTermMonths ?? 60}
                  onChange={(v) => updateSetting('leaseTermMonths', v)}
                />
              </>
            )}

            {/* Interest payable was an input held flat, so a company repaying a
                loan went on showing interest on money it had given back. */}
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={settings.deriveDebtSchedule ?? false}
                onChange={(e) =>
                  updateSetting('deriveDebtSchedule', e.target.checked)
                }
                className="mt-0.5"
              />
              <span>
                <span className="text-sm font-medium text-gray-700">
                  Derive debt schedule
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  Rolls the borrowings with the drawdowns and repayments and
                  charges interest on the balance, so the interest follows the
                  loan down. Replaces the interest payable figure rather than
                  adding to it.
                </span>
              </span>
            </label>

            {settings.deriveDebtSchedule && (
              <NumberField
                label="Debt interest rate"
                hint="The rate the borrowings bear."
                value={settings.debtInterestRate ?? 0.06}
                onChange={(v) => updateSetting('debtInterestRate', v)}
                step={0.01}
                suffix={`= ${((settings.debtInterestRate ?? 0.06) * 100).toFixed(1)}%`}
              />
            )}
            <NumberField
              label="Depreciation period (months)"
              hint="Forecast years with no D&A charge entered on Fixed Assets are charged straight-line over this period."
              value={settings.depreciationPeriodMonths}
              onChange={(v) => updateSetting('depreciationPeriodMonths', v)}
              step={1}
            />
            {/* The old "Interest rate" field here drove nothing (interest is the interest payable
                line, or the debt schedule's rate above). These two do: 0 is off. */}
            <NumberField
              label="Forecast cost of goods ratio"
              hint="Forecast goods sold as a share of revenue, where none is entered. 0 uses the last filed year's own margin."
              value={settings.forecastCogsRatio ?? 0}
              onChange={(v) => updateSetting('forecastCogsRatio', v || null)}
              step={0.01}
              suffix={settings.forecastCogsRatio ? `= ${(settings.forecastCogsRatio * 100).toFixed(1)}%` : 'off'}
            />
            <NumberField
              label="Forecast staff welfare rate"
              hint="Staff welfare as a share of indirect staff costs, in forecast periods. 0 is none."
              value={settings.forecastWelfareRate ?? 0}
              onChange={(v) => updateSetting('forecastWelfareRate', v || null)}
              step={0.01}
              suffix={settings.forecastWelfareRate ? `= ${(settings.forecastWelfareRate * 100).toFixed(1)}%` : 'off'}
            />
          </div>
        </section>

        {/* Inputs that are single figures, not a series: working capital, and the position the
            first year opens on. Each was in the model and could be changed nowhere. */}
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Working capital and tax</h3>
          <div className="space-y-3">
            <NumberField
              label="Debtor days"
              hint="Forecast trade debtors: days of revenue (incl. VAT) still to be received."
              value={model.inputs.controlAccount.debtorDays}
              onChange={(v) => updateInputValue('controlAccount.debtorDays', v)}
              step={1}
            />
            <NumberField
              label="Creditor days"
              hint="Forecast trade creditors: days of purchases (incl. VAT) still to be paid."
              value={model.inputs.controlAccount.creditorDays}
              onChange={(v) => updateInputValue('controlAccount.creditorDays', v)}
              step={1}
            />
            <NumberField
              label="Stock days"
              hint="Forecast closing stock as days of cost of sales. 0 = stock not modelled (purchases equal goods sold)."
              value={model.inputs.controlAccount.stockDays ?? 0}
              onChange={(v) => updateInputValue('controlAccount.stockDays', v)}
              step={1}
            />
            <NumberField
              label="VAT rate"
              value={model.inputs.controlAccount.vatRate}
              onChange={(v) => updateInputValue('controlAccount.vatRate', v)}
              step={0.01}
              suffix={`= ${(model.inputs.controlAccount.vatRate * 100).toFixed(1)}%`}
            />
            <NumberField
              label="Share of the tax charge owed at the year end"
              hint="1 = the whole year's charge is still owed (the template's assumption)."
              value={model.inputs.controlAccount.taxCreditorRatio ?? 1}
              onChange={(v) => updateInputValue('controlAccount.taxCreditorRatio', v)}
              step={0.05}
            />
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Opening position</h3>
          <p className="mb-3 text-xs text-gray-500">
            The balances the first year opens on. Liabilities are entered negative, as the control
            account holds them.
          </p>
          <div className="space-y-3">
            <NumberField label="Share capital" value={model.inputs.opening.shareCapital}
              onChange={(v) => updateInputValue('opening.shareCapital', v)} step={1} />
            <NumberField label="Share-based payment reserve" value={model.inputs.opening.shareBasedPaymentReserve}
              onChange={(v) => updateInputValue('opening.shareBasedPaymentReserve', v)} step={1} />
            <NumberField label="Other reserves (share premium and the rest)" value={model.inputs.opening.otherReserves ?? 0}
              onChange={(v) => updateInputValue('opening.otherReserves', v)} step={1} />
            <NumberField label="Retained earnings" value={model.inputs.opening.retainedEarnings}
              onChange={(v) => updateInputValue('opening.retainedEarnings', v)} step={1} />
            <NumberField label="Tax losses brought forward" value={model.inputs.opening.taxLosses ?? 0}
              onChange={(v) => updateInputValue('opening.taxLosses', v)} step={1} />
            <NumberField label="Trade debtors" value={model.inputs.controlAccount.openingTradeDebtors}
              onChange={(v) => updateInputValue('controlAccount.openingTradeDebtors', v)} step={1} />
            <NumberField label="Trade creditors (negative)" value={model.inputs.controlAccount.openingTradeCreditors}
              onChange={(v) => updateInputValue('controlAccount.openingTradeCreditors', v)} step={1} />
            <NumberField label="Corporation tax owed (negative)" value={model.inputs.controlAccount.openingCorporationTax ?? 0}
              onChange={(v) => updateInputValue('controlAccount.openingCorporationTax', v)} step={1} />
            <NumberField label="VAT (owed negative)" value={model.inputs.controlAccount.openingVat ?? 0}
              onChange={(v) => updateInputValue('controlAccount.openingVat', v)} step={1} />
          </div>
        </section>

      </div>
    </div>
  );
}
